import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { FormControl, FormLabel, RadioGroup, Radio, FormControlLabel as FormControlLabelMUI } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Calendar from '@/components/UI/Calendar/Calendar';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import MandatoryLabel from '@/components/UI/MandatoryLabel/MandatoryLabel';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';
import Spinner from '@/components/UI/Spinner/Spinner';
import { getNewState, validateFieldDependingOfSwitcher, getErrorMessage } from '@/helpers/campaignValidations';
import { CHINA_COMBO_ORDER, CLASSIFICATION, classificationSelectOptions, fieldTypes, HELP_AREA, TAB_FORMS_OPTIONS } from '@/helpers/constants';
import { stringToDate, checkDate, formatSummaryUser } from '@/helpers/utils';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import useError from '@/hooks/useError';
import useToastContext from '@/hooks/useToastContext';
import useWarningField from '@/hooks/useWarningField';
import {
  setCampaignClassification, setOriginalValues,
  setClassificationValidations, setMinActiveValidations,
} from '@/redux/Campaign/campaign.action';
import {
  getCampaignClassification, getOriginalValues, getClassificationValidations,
  getCampaignInfo, getCampaignValidationsActive, getIsVANCR,
} from '@/redux/Campaign/campaign.reducer';
import { setClassificationForms } from '@/redux/Forms/forms.action';
import { getClassificationForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import styles from './Classification.module.scss';

const useStyles = makeStyles(() => ({
  formControlLabel: {
    alignItems: 'unset',
    marginLeft: 0,
  },
  consultationType: { fontSize: 14 },
  twoColumns: { width: '50%' },
}));

const Classification = (props) => {
  const { campaignInfo, classification, setClassification, forms, setForms, activeValidations,
    setActiveMinLengthValidations, originalValues, setOriginValues, classifValidations, setClassifValidations, headerLanguage,
    setHelpArea, subCR, isVANCR } = props;
  const { t } = useTranslation();
  const { id, version } = useParams();
  const isLoading = !forms.classification || !classification?.id;
  const checkDisabled = useCheckDisabled(true) || subCR;
  const { addToast } = useToastContext();
  const { handleCriticalError } = useError();
  const classes = useStyles();
  const { detectLanguage, detectLanguageTranslation } = useDetectLanguage();
  const potentiallyCustomerWarning = useWarningField([CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.POTENTIALLY_SCE_RELEVANT], fieldTypes.CUSTOM_ARRAY);
  const isRelevanceCommentDisabled = checkDisabled || !classification?.customerRelevance;

  const onCopyOriginValues = (content) => {
    const copyValues = {
      values: content,
      hasChange: false,
    };
    setOriginValues(copyValues);
  };

  useEffect(() => {
    if (!forms.classification) {
      campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_CLASSIFICATION).then((res) => {
        setForms(res.form);
      }).catch(handleCriticalError);
    }

    if (subCR) {
      campaignServices.getSubCampaign(id, `${isVANCR ? 'van_' : ''}classification`, version)
        .then((data) => {
          onCopyOriginValues(data?.module);
          setClassification(data?.module);
        })
        .catch(handleCriticalError);
    } else {
      campaignServices.getCampaignRetry(id, CLASSIFICATION.CLASSIFICATION, version, campaignInfo.type, campaignInfo.creationDate)
        .then((res) => {
          onCopyOriginValues(res);
          setClassification(res);
        })
        .catch(handleCriticalError);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSetOriginValues = () => {
    const values = {
      ...originalValues,
      hasChange: true,
    };
    setOriginValues(values);
  };

  const activeShowMinError = (label, value) => {
    const comments = [CLASSIFICATION.CONSULTATION_COMMENT, CLASSIFICATION.RELEVANCE_COMMENT, CLASSIFICATION.RISK_COMMENT];

    if (value?.length >= 8 && comments.includes(label)) {
      setActiveMinLengthValidations(label);
    }
  };

  const validateField = (label, value) => {
    const fields = [CLASSIFICATION.RELEVANCE_COMMENT, CLASSIFICATION.CUSTOMER_RELEVANCE, CLASSIFICATION.POTENTIALLY_SCE_RELEVANT,
      CLASSIFICATION.CONSULTATION_NAME, CLASSIFICATION.CONSULTATION_COMMENT, CLASSIFICATION.CRASH_CERTIFICATION_VEHICLES,
      CLASSIFICATION.DEVELOPMENT_VEHICLE_POOLING];

    activeShowMinError(label, value);

    if (fields.includes(label)) {
      let newState = classifValidations;
      const FieldsDependsOfSwicher = [CLASSIFICATION.CONSULTATION_NAME, CLASSIFICATION.CONSULTATION_COMMENT];

      if (label === CLASSIFICATION.TEAM_CONSULTED) {
        FieldsDependsOfSwicher.forEach((currentField) => {
          newState = {
            ...newState,
            [headerLanguage]: {
              ...newState[headerLanguage],
              [currentField]: validateFieldDependingOfSwitcher(classifValidations?.[headerLanguage], currentField, '', classification.teamConsulted, false),
            },
          };
        });
      } else if (label === CLASSIFICATION.RELEVANCE_COMMENT) {
        newState = {
          [headerLanguage]: validateFieldDependingOfSwitcher(classifValidations?.[headerLanguage], label, value,
            !classification.customerRelevanceChosen, activeValidations?.min?.relevanceComment),
        };
      } else if (FieldsDependsOfSwicher.some((currentLabel) => currentLabel === label)) {
        newState = {
          [headerLanguage]: validateFieldDependingOfSwitcher(classifValidations?.[headerLanguage], label, value,
            classification.teamConsulted, activeValidations?.min?.[label]),
        };
      } else {
        newState = {
          [headerLanguage]: getNewState(classifValidations?.[headerLanguage], label, value, null,
            activeValidations?.min?.[label]),
        };
      }
      setClassifValidations(newState);
    }
  };

  const getNewStateOnchangeTeamConsulted = (value) => (value
    ? {
      ...classification,
      teamConsulted: value,
    }
    : {
      ...classification,
      teamConsulted: value,
      consultationDate: '',
      consultantName: { code: '', department: '', summary: '' },
      consultationComment: { ...classification.consultationComment, [headerLanguage]: '' },
    });

  const updateStateComment = (label, value) => {
    validateField(label, value);
    const newState = {
      ...classification,
      [label]: {
        ...classification[label],
        [headerLanguage]: value,
      },
    };
    onSetOriginValues();
    setClassification(newState);
  };

  const getNewStateOnchangeCustomerRelevanceChosen = (value) => (value
    ? {
      ...classification,
      customerRelevanceChosen: value,
      potentiallySCECertificationRelevant: [],
    }
    : {
      ...classification,
      customerRelevanceChosen: value,
      customerRelevance: '',
    });

  const updateState = (label, value) => {
    validateField(label, value);
    let newState = {};

    if (label === CLASSIFICATION.TEAM_CONSULTED) {
      newState = getNewStateOnchangeTeamConsulted(value);
    } else if (label === CLASSIFICATION.CUSTOMER_RELEVANCE_CHOSEN) {
      newState = getNewStateOnchangeCustomerRelevanceChosen(value);

      if (!value) {
        newState.relevanceComment[headerLanguage] = '';
      }
    } else {
      newState = {
        ...classification,
        [label]: value,
      };
    }
    onSetOriginValues();
    setClassification(newState);
  };

  const handleChangeClassification = (ev) => {
    const rdgClassification = (ev.target.value === 'true');
    updateState(CLASSIFICATION.CUSTOMER_RELEVANCE_CHOSEN, rdgClassification);
  };

  const handleOptionsDependencies = (selectedOptions) => {
    const isCertificationSelected = selectedOptions.includes(CLASSIFICATION.SELECT_OPTIONS.POT_CERTIF);
    const isConformitySelected = selectedOptions.includes(CLASSIFICATION.SELECT_OPTIONS.POT_REG_CONF);

    if (isCertificationSelected && !isConformitySelected) {
      const previousSelectedValues = classification?.potentiallySCECertificationRelevant || [];

      if (previousSelectedValues.includes(CLASSIFICATION.SELECT_OPTIONS.POT_CERTIF)) {
        addToast(t('alert.potCertificationUnselected'), 'Warning');

        return selectedOptions.filter((selectedValue) => selectedValue !== CLASSIFICATION.SELECT_OPTIONS.POT_CERTIF);
      }

      return [...selectedOptions, CLASSIFICATION.SELECT_OPTIONS.POT_REG_CONF];
    }

    return selectedOptions;
  };

  const handleChangeSelectClassification = (value) => {
    validateField(CLASSIFICATION.POTENTIALLY_SCE_RELEVANT, value);

    const finalSelection = handleOptionsDependencies(value);

    let newState = {
      ...classification,
      potentiallySCECertificationRelevant: finalSelection,
    };

    if (finalSelection.find((val) => classificationSelectOptions.includes(val))?.length > 0) {
      newState = {
        ...newState,
        customerRelevance: 'Level1',
      };
    } else if (finalSelection.length === 0) {
      newState = {
        ...newState,
        customerRelevance: '',
      };
      newState.relevanceComment[headerLanguage] = '';
    }
    onSetOriginValues();
    setClassification(newState);
  };

  const handleDeleteItem = (item) => {
    const types = classification?.potentiallySCECertificationRelevant?.filter((type) => type !== item);
    handleChangeSelectClassification(types);
  };

  const handleChangeAffectsChina = (affectsChina) => {
    if (affectsChina === 'Yes') {
      addToast(t('classification.affectsChinaAlert'), 'Fixed');
    }

    updateState(CLASSIFICATION.AFFECTS_CHINA, affectsChina);
  };

  const updateUser = (label, userSelected) => {
    const contactPerson = {
      code: userSelected?.selected ? userSelected.userCode : '',
      department: userSelected?.selected ? userSelected.department : '',
      summary: userSelected?.selected ? formatSummaryUser(userSelected) : '',
    };

    updateState(label, contactPerson);
  };

  const getHelperText = (consulted, comment, maxLength) => {
    let text = consulted ? t(getErrorMessage(classifValidations?.[headerLanguage]?.[comment]?.errors), { maxLength }) : null;

    if (classification?.[comment]?.[headerLanguage]?.length === maxLength) {
      text = t('validations.maxLength', { maxLength });
    }

    return text;
  };

  const handleChangeConsultation = (value, label) => {
    const newState = value === CLASSIFICATION.NO_CONSULTATION
      ? {
        ...classification,
        consultationType: value,
        consultationDate: '',
        consultantName: { code: '', department: '', summary: '' },
        consultationComment: { ...classification.consultationComment, [headerLanguage]: '' },
      }
      : { ...classification, [label]: value };
    onSetOriginValues();
    setClassification(newState);
  };

  const chinaOptions = CHINA_COMBO_ORDER.reduce((acc, newKey) => ({ ...acc, [newKey]: forms.affectsChina?.options[newKey] }), {});
  const consultedOptions = forms.consultationType?.options || {};
  const blockingOptions = forms.blockingRecommendation?.options || {};
  const { consultationType } = classification || {};
  const isConsultationActive = consultationType ? consultationType !== CLASSIFICATION.NO_CONSULTATION : false;

  const onBlur = (name, value) => {
    detectLanguage(value);

    if (originalValues?.hasChange) {
      detectLanguageTranslation(name);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <AccordionPanel
            title={t('classification.assessment')}
            handleClick={() => setHelpArea(HELP_AREA.RELEVANCE)}
          >
            <div className={styles.TwoColumns}>
              <FormControl disabled={checkDisabled} component="fieldset" className={`${potentiallyCustomerWarning ? styles.RadioWarning : ''}`}>
                <FormLabel component="legend">{t('classification.classification')}</FormLabel>
                <RadioGroup
                  row
                  aria-label="position"
                  name="position"
                  onChange={handleChangeClassification}
                  value={classification.customerRelevanceChosen}
                >
                  <FormControlLabelMUI value={false} control={<Radio color="primary" />} label={t('classification.potentiallySCE')} />
                  <FormControlLabelMUI value control={<Radio color="primary" />} label={t('classification.customerRelevancy')} />
                </RadioGroup>
              </FormControl>
              <div className={styles.ContainerFlex}>
                <MultiSelection
                  fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.POTENTIALLY_SCE_RELEVANT]}
                  otherConditionInvalid={{ and: true, condition: !classification?.customerRelevanceChosen }}
                  isMandatory
                  handleDeleteItem={handleDeleteItem}
                  id="select-classification"
                  label={t('classification.select')}
                  disabled={checkDisabled || classification.customerRelevanceChosen}
                  options={forms.classification.options}
                  value={classification.potentiallySCECertificationRelevant}
                  handleChange={(ev) => handleChangeSelectClassification(ev.target.value)}
                  formControlClass={classes.twoColumns}
                  language={headerLanguage}
                />
                <Dropdown
                  fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CUSTOMER_RELEVANCE]}
                  isMandatory
                  id="customer-relevancy"
                  label={t('classification.customerRelevancy')}
                  disabled={checkDisabled || !classification.customerRelevanceChosen}
                  options={forms.customerRelevancy.options}
                  value={classification.customerRelevance}
                  handleChange={(event) => { updateState(CLASSIFICATION.CUSTOMER_RELEVANCE, event.target.value); }}
                  formControlClass={classes.twoColumns}
                  language={headerLanguage}
                />
              </div>
            </div>
            <div
              className={styles.TwoColumns}
              onClick={() => ((isRelevanceCommentDisabled && !checkDisabled) ? addToast(t('validations.classificationRequired'), 'Warning') : null)}
            >
              <InputText
                disabled={isRelevanceCommentDisabled}
                fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.RELEVANCE_COMMENT]}
                otherConditionInvalid={{ and: true, condition: !classification?.customerRelevanceChosen }}
                isMandatory={!classification.customerRelevanceChosen}
                multiline
                rows={5}
                maxRows={5}
                maxLength={512}
                id="classification-comment"
                label={t('classification.relevanceComment')}
                handleChange={(ev) => { updateStateComment(CLASSIFICATION.RELEVANCE_COMMENT, ev.target.value); }}
                value={classification.relevanceComment[headerLanguage] || ''}
                onBlur={(ev) => onBlur(CLASSIFICATION.RELEVANCE_COMMENT, ev.target.value)}
                helperText={[
                  ...((!checkDisabled && getHelperText(!classification.customerRelevanceChosen, CLASSIFICATION.RELEVANCE_COMMENT, 512))
                    ? [t(getHelperText(!classification.customerRelevanceChosen, CLASSIFICATION.RELEVANCE_COMMENT, 512))]
                    : []),
                ]}
              />
            </div>
          </AccordionPanel>
          <AccordionPanel
            title={t('classification.developmentVehicleAssessment')}
            handleClick={() => setHelpArea(HELP_AREA.DEVELOPMENT_VEHICLE_ASSESSMENT)}
          >
            <div className={styles.DevelopmentVehicleAssessment}>
              <div className={styles.FieldPair}>
                <FormLabel className={styles.FieldLabel}>
                  {t('classification.crashCertificationVehicles')}
                  <MandatoryLabel marginRight={0} />
                </FormLabel>
                <Dropdown
                  disabled={checkDisabled}
                  fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CRASH_CERTIFICATION_VEHICLES]}
                  id="crash-certification-vehicles"
                  placeholder={t('classification.selectPlaceholder')}
                  options={forms?.crashCertificationVehicles?.options ?? {}}
                  value={classification?.crashCertificationVehicles || 'placeholder'}
                  handleChange={(ev) => updateState(CLASSIFICATION.CRASH_CERTIFICATION_VEHICLES, ev.target.value)}
                  language={headerLanguage}
                  formControlClass={styles.FieldDropdown}
                />
              </div>
              <div className={styles.FieldPair}>
                <FormLabel className={styles.FieldLabel}>
                  {t('classification.developmentVehiclePooling')}
                  <MandatoryLabel marginRight={0} />
                </FormLabel>
                <Dropdown
                  disabled={checkDisabled}
                  fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.DEVELOPMENT_VEHICLE_POOLING]}
                  id="development-vehicle-pooling"
                  placeholder={t('classification.selectPlaceholder')}
                  options={forms?.developmentVehiclePooling?.options ?? {}}
                  value={classification?.developmentVehiclePooling || 'placeholder'}
                  handleChange={(ev) => updateState(CLASSIFICATION.DEVELOPMENT_VEHICLE_POOLING, ev.target.value)}
                  language={headerLanguage}
                  formControlClass={styles.FieldDropdown}
                />
              </div>
            </div>
          </AccordionPanel>
          <AccordionPanel
            title={t('classification.consultation')}
            handleClick={() => setHelpArea(HELP_AREA.CONSULTATION)}
          >
            <div className={styles.FourColumns}>
              <Dropdown
                disabled={checkDisabled}
                fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CONSULTATION_TYPE]}
                id="consultation-type"
                label={t('classification.consultationType')}
                isMandatory={isVANCR}
                options={consultedOptions || {}}
                value={classification.consultationType || ''}
                handleChange={(event) => { handleChangeConsultation(event.target.value, CLASSIFICATION.CONSULTATION_TYPE); }}
                formControlClass={classes.FourColumns}
                language={headerLanguage}
              />
            </div>
            <div className={styles.FourColumns}>
              <Calendar
                disabled={checkDisabled || !isConsultationActive}
                fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CONSULTATION_DATE]}
                id="date-picker-consultation-date"
                label={t('classification.consultationDate')}
                margin="none"
                isMandatory={isConsultationActive}
                handleChange={(date) => checkDate({ date, label: CLASSIFICATION.CONSULTATION_DATE, handleChange: updateState })}
                value={stringToDate(classification.consultationDate)}
              />
              <InputUser
                fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CONSULTATION_NAME]}
                userFieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CONSULTATION_NAME]}
                otherConditionInvalid={{ and: true, condition: classification?.consultantName }}
                isMandatory={isConsultationActive}
                disabled={checkDisabled || !isConsultationActive}
                id="input-consultant-name"
                label={t('classification.consultantName')}
                handleChange={(ev) => { updateState(CLASSIFICATION.CONSULTATION_NAME, ev.target.value); }}
                updateUserSelected={(userSelected) => updateUser(CLASSIFICATION.CONSULTATION_NAME, userSelected)}
                value={classification?.consultantName?.summary}
                formControlClass={classes.fourColumns}
                code={classification?.consultantName?.code}
                clear={() => { updateUser(CLASSIFICATION.CONSULTATION_NAME, null); }}
              />
            </div>
            <div className={styles.TwoColumns}>
              <InputText
                fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.CONSULTATION_COMMENT]}
                otherConditionInvalid={{ and: true, condition: classification?.consultationComment }}
                isMandatory={isConsultationActive}
                multiline
                rows={5}
                maxRows={5}
                maxLength={256}
                disabled={checkDisabled || !isConsultationActive}
                id="input-consultation-comment"
                label={t('classification.consultationComment')}
                handleChange={(ev) => { updateStateComment(CLASSIFICATION.CONSULTATION_COMMENT, ev.target.value); }}
                value={classification?.consultationComment?.[headerLanguage] || ''}
                onBlur={(ev) => onBlur(CLASSIFICATION.CONSULTATION_COMMENT, ev.target.value)}
                helperText={[
                  ...((!checkDisabled && getHelperText(classification?.teamConsulted, CLASSIFICATION.CONSULTATION_COMMENT, 256))
                    ? [t(getHelperText(classification?.teamConsulted, CLASSIFICATION.CONSULTATION_COMMENT, 256))]
                    : []),
                ]}
              />
            </div>
          </AccordionPanel>
          <AccordionPanel
            title={t('classification.blockingRecommendation')}
            handleClick={() => setHelpArea(HELP_AREA.BLOCKING_RECOMENDATION)}
          >
            <div className={styles.FourColumns}>
              <Dropdown
                disabled={checkDisabled}
                id="classification-blockingRecommendation"
                label={t('classification.blockingRecommendation')}
                options={blockingOptions || {}}
                value={classification.blockingRecommendation || ''}
                handleChange={(event) => { handleChangeConsultation(event.target.value, CLASSIFICATION.BLOCKING_RECOMENDATION); }}
                formControlClass={classes.FourColumns}
                language={headerLanguage}
              />
            </div>
          </AccordionPanel>

          {classification.affectsChina && (
            <AccordionPanel
              title={t('classification.chinesseMarket')}
              handleClick={() => setHelpArea(HELP_AREA.AFFECTS_CHINA)}
            >
              <Dropdown
                disabled={checkDisabled}
                fieldPath={[CLASSIFICATION.CLASSIFICATION, CLASSIFICATION.AFFECTS_CHINA]}
                id="affects-china"
                label={t('classification.china')}
                options={chinaOptions || {}}
                value={classification.affectsChina}
                handleChange={(ev) => handleChangeAffectsChina(ev.target.value)}
                formControlClass={classes.twoColumns}
                language={headerLanguage}
              />
            </AccordionPanel>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
  classification: getCampaignClassification(state),
  forms: getClassificationForms(state),
  originalValues: getOriginalValues(state),
  classifValidations: getClassificationValidations(state),
  activeValidations: getCampaignValidationsActive(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setClassification: setCampaignClassification,
  setForms: setClassificationForms,
  setOriginValues: setOriginalValues,
  setClassifValidations: setClassificationValidations,
  setActiveMinLengthValidations: setMinActiveValidations,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(Classification);
